import React from 'react';
import { AccountCard } from './AccountCard';
import { Container, GridContainer } from './styles';
import { GcvZeroState, GcvDataTable } from '../../../lib';
import {
  MinifiedDispensary,
  DueDiligenceStatus,
  DueDiligenceRequirementStatus,
  DueDiligenceDocumentStatus,
  DueDiligenceRequirements,
  Dispensary,
  DueDiligenceRequirement,
} from '@gcv/shared';
import { getDispensaryLicenseType, getMinifiedDispensaryLicenseType } from 'libs/react-ui/src/util';

interface Props {
  accounts: any[];
  emitData?: (e) => any;
  tableView: boolean;
  tableColumns: any[];
  sortKey: string;
  isPending?: boolean;
  noAccountsZeroStateText: string;
  isFiltered: boolean;
}

export const AccountCardContainer = ({
  accounts,
  emitData,
  tableView,
  tableColumns,
  sortKey,
  isPending,
  noAccountsZeroStateText,
  isFiltered,
}: Props) => {
  const createProps = (dispensary: any) => {
    return {
      name: dispensary.name,
      complianceScore: dispensary.compliance_score,
      logo: '../../../../assets/icon_account_circle.svg',
      isApproved: dispensary.due_diligence.due_diligence_status === DueDiligenceStatus.BANK_APPROVED,
      isAwaitingReview: dispensary.due_diligence.due_diligence_status === DueDiligenceStatus.BANK_AWAITING_REVIEW,
    };
  };

  const handleButtonClicked = dispensary => {
    return () => {
      emitData(dispensary.id);
    };
  };

  const handleRowClicked = dispensary => {
    emitData(dispensary.id);
  };

  const renderCards = () => {
    return accounts.map(dispensary => {
      const accountCardProps = createProps(dispensary);
      return <AccountCard key={dispensary.id} {...accountCardProps} onButtonClick={handleButtonClicked(dispensary)} />;
    });
  };

  const data = accounts.map((dispensary: any) => {
    //get these three special properties either from minified dispensaries call,
    //or from the full dispensary object in cases where the dispensary gets updated in the store
    const licenseType = dispensary.license_summary
      ? getMinifiedDispensaryLicenseType(dispensary)
      : getDispensaryLicenseType(dispensary);

    const inProgress: boolean = dispensary.license_summary
      ? dispensary.license_summary.bank_review_in_progress
      : dispensary.due_diligence.account_owners_section_bank_review_complete ||
        dispensary.due_diligence.business_details_section_bank_review_complete ||
        dispensary.due_diligence.operational_details_section_bank_review_complete ||
        dispensary.due_diligence.due_diligence_documentation_section_bank_review_complete ||
        Object.keys(dispensary.due_diligence.requirements).some(reqId => {
          return (
            dispensary.due_diligence.requirements[reqId].status === DueDiligenceRequirementStatus.REVIEWED ||
            Object.keys(dispensary.due_diligence.requirements[reqId].documents).some(
              docId =>
                dispensary.due_diligence.requirements[reqId].documents[docId].status !==
                DueDiligenceDocumentStatus.PENDING
            )
          );
        });

    const documentsCount = dispensary.license_summary
      ? dispensary.license_summary.document_count
      : Object.values(dispensary.due_diligence.requirements).reduce(
          (total: number, current: DueDiligenceRequirement) => {
            total = total + Object.keys(current.documents).length;
          },
          0
        );

    return {
      date_updated: dispensary.date_updated, // TODO: determine this some other way
      compliance_score: dispensary.compliance_score,
      submitted_date: dispensary.due_diligence.bank_submitted_date,
      reviewed_date: dispensary.due_diligence.bank_reviewed_on,
      due_diligence: dispensary.due_diligence,
      date_created: dispensary.date_created,
      license_type: licenseType,
      city: dispensary.city,
      state: dispensary.state,
      name: dispensary.name,
      id: dispensary.id,
      bank_review_in_progress: inProgress,
      document_count: documentsCount,
    };
  });

  return (
    <Container>
      {accounts.length > 0 ? (
        tableView ? (
          <GcvDataTable
            data={data}
            columns={tableColumns}
            onRowClicked={handleRowClicked}
            defaultSortField={sortKey}
            defaultSortAsc={false}
            keyField="id"
            subHeaderAlign="left"
            paginationPerPage={8}
            pointerOnHover={true}
          />
        ) : (
          <GridContainer>{renderCards()}</GridContainer>
        )
      ) : isFiltered ? (
        <GcvZeroState
          type="basic"
          headerText={'There are no accounts that match your filter'}
          subText={'Use the search bar to change or remove your filter'}
        />
      ) : (
        <GcvZeroState
          type="basic"
          headerText={noAccountsZeroStateText}
          subText={"Don't worry, Green Check will notify you when there's a new account to review"}
        />
      )}
    </Container>
  );
};
